import { API_BASE_URL } from "../config/env";
import { clearStoredAuth, readAuthToken, updateStoredAuthToken } from "../auth/authStorage";

// NOTE: use centralized API_BASE_URL which has a runtime fallback for production builds.
const BASE = API_BASE_URL;
const AUTH_TOKEN_UPDATED_EVENT = "bs:auth-token-updated";
const AUTH_INVALIDATED_EVENT = "bs:auth-invalidated";

let refreshPromise = null;

const DEFAULT_TIMEOUT_MS = Math.max(5000, Number(process.env.REACT_APP_API_TIMEOUT_MS || 30000) || 30000);

function assertSafeApiPath(path) {
    const value = String(path || "").trim();
    if (!value) throw new Error("API path is required");
    if (/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(value) || value.startsWith("//")) {
        throw new Error("External API URLs are not allowed in request()");
    }
    return value.startsWith("/") ? value : `/${value}`;
}

function url(path) {
    return `${BASE}${assertSafeApiPath(path)}`;
}

function writeToken(token) {
    updateStoredAuthToken(token);

    try {
        if (typeof window !== "undefined") {
            window.dispatchEvent(
                new CustomEvent(AUTH_TOKEN_UPDATED_EVENT, {
                    detail: { token: token ?? null },
                })
            );
        }
    } catch {}
}

function invalidateAuth(reason = "unknown") {
    clearStoredAuth();

    try {
        if (typeof window !== "undefined") {
            window.dispatchEvent(
                new CustomEvent(AUTH_INVALIDATED_EVENT, {
                    detail: { reason },
                })
            );
        }
    } catch {}
}

function canRefreshForPath(path) {
    const p = String(path || "").trim();
    return !(p.startsWith("/auth/") || p.startsWith("auth/"));
}

async function parsePayload(res) {
    const ct = res.headers.get("content-type") || "";
    try {
        return ct.includes("application/json") ? await res.json() : await res.text();
    } catch {
        return null;
    }
}

async function refreshSession() {
    if (!refreshPromise) {
        refreshPromise = (async () => {
            const res = await fetch(url("/auth/refresh"), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({}),
                credentials: "include",
            });
            if (!res.ok) {
                const payload = await parsePayload(res);
                const err = new Error(
                    typeof payload === "string"
                        ? payload
                        : (payload?.message || payload?.error || `Refresh failed: HTTP ${res.status}`)
                );
                err.status = res.status;
                if (payload && typeof payload === "object") Object.assign(err, payload);
                throw err;
            }

            const payload = await parsePayload(res);
            const token =
                payload && typeof payload === "object"
                    ? (payload.token ?? payload.accessToken ?? payload.jwt ?? null)
                    : null;

            // Если backend перешёл в cookie-only режим, убираем stale bearer.
            writeToken(token);
        })().finally(() => {
            refreshPromise = null;
        });
    }

    return refreshPromise;
}

export async function request(path, { method = "GET", json, headers, _skipRefresh = false, timeoutMs = DEFAULT_TIMEOUT_MS, signal, ...rest } = {}) {
    const token = readAuthToken();
    const hasAuthHeader = headers && (headers.Authorization || headers.authorization);
    const controller = !signal && typeof AbortController !== "undefined" ? new AbortController() : null;
    const timer = controller && timeoutMs > 0 ? setTimeout(() => controller.abort(), timeoutMs) : null;

    let res;
    try {
        res = await fetch(url(path), {
            method,
            headers: {
                Accept: "application/json, text/plain, */*",
                "X-Requested-With": "XMLHttpRequest",
                ...(json ? { "Content-Type": "application/json" } : {}),
                ...(token && !hasAuthHeader ? { Authorization: `Bearer ${token}` } : {}),
                ...(headers || {}),
            },
            body: json !== undefined ? JSON.stringify(json) : rest.body,
            credentials: "include",
            signal: signal || controller?.signal,
            ...rest,
        });
    } catch (err) {
        if (err?.name === "AbortError") {
            const timeoutError = new Error("Превышено время ожидания ответа сервера");
            timeoutError.status = 408;
            throw timeoutError;
        }
        throw err;
    } finally {
        if (timer) clearTimeout(timer);
    }

    if (res.status === 401 && !_skipRefresh && canRefreshForPath(path)) {
        try {
            await refreshSession();
            return request(path, { method, json, headers, _skipRefresh: true, timeoutMs, signal, ...rest });
        } catch (refreshErr) {
            const st = refreshErr?.status ?? null;
            if (st === 401 || st === 403) {
                invalidateAuth("refresh-denied");
            }
        }
    }

    if (!res.ok) {
        const payload = await parsePayload(res);

        const err = new Error(
            typeof payload === "string"
                ? payload
                : (payload?.message || payload?.error || `HTTP ${res.status}`)
        );
        err.status = res.status;
        if (payload && typeof payload === "object") Object.assign(err, payload);
        throw err;
    }

    if (res.status === 204 || res.status === 205) return null;
    const ct = res.headers.get("content-type") || "";
    return ct.includes("application/json") ? res.json() : res.text();
}
